import * as React from "react"
import { cn } from "cn"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"

type WorkSurfaceFrameProps = Omit<React.ComponentProps<"section">, "title"> & {
  title: React.ReactNode
  description?: React.ReactNode
  status?: React.ReactNode
  actions?: React.ReactNode
  onBack?: () => void
  backLabel?: string
  contentClassName?: string
}

/** A view of real work state, not a controller or an authorization boundary. */
function WorkSurfaceFrame({
  title,
  description,
  status,
  actions,
  onBack,
  backLabel = "Close tool",
  contentClassName,
  children,
  className,
  ...props
}: WorkSurfaceFrameProps) {
  return (
    <section data-slot="work-surface-frame" className={cn("flex min-h-0 min-w-0 flex-col bg-card text-card-foreground", className)} {...props}>
      <header className="sticky top-0 z-10 flex items-start gap-4 border-b border-border bg-card p-4 sm:p-6">
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          {description && <div className="text-sm text-muted-foreground">{description}</div>}
          {status && <div role="status" className="pt-1 text-sm">{status}</div>}
        </div>
        {onBack && <Button type="button" variant="ghost" size="icon" className="shrink-0" aria-label={backLabel} title={backLabel} onClick={onBack}><X /></Button>}
      </header>
      <div data-slot="work-surface-content" className={cn("min-h-0 min-w-0 flex-1 p-4 sm:p-6", contentClassName)}>{children}</div>
      {actions && <footer data-slot="work-surface-actions" className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4 sm:px-6">{actions}</footer>}
    </section>
  )
}

export { WorkSurfaceFrame }
export type { WorkSurfaceFrameProps }
